import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ── Brand colours ────────────────────────────────────────────────────────────
const BRAND_DARK   = [30, 41, 59];    // slate-800
const BRAND_ACCENT = [59, 130, 246];  // blue-500
const TEXT_DARK    = [15, 23, 42];    // slate-900
const TEXT_MID     = [100, 116, 139]; // slate-500
const BG_LIGHT     = [248, 250, 252]; // slate-50
const WHITE        = [255, 255, 255];
const RED          = [239, 68, 68];
const AMBER        = [245, 158, 11];
const GREEN        = [34, 197, 94];

// ── Formatters ───────────────────────────────────────────────────────────────
const fmt = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr)?.toLocaleDateString('en-GB'); } catch { return '—'; }
};

const currency = (val) => {
  if (val == null || isNaN(val)) return '£0';
  return '£' + Number(val)?.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const pct = (num, den) => {
  if (!den || den === 0) return '0%';
  return Math.round((num / den) * 100) + '%';
};

const severityColor = (sev) => {
  if (!sev) return TEXT_MID;
  const s = sev?.toLowerCase();
  if (s === 'critical' || s === 'high') return RED;
  if (s === 'medium') return AMBER;
  return GREEN;
};

const statusColor = (status) => {
  if (!status) return TEXT_MID;
  const s = status?.toLowerCase();
  if (s === 'open' || s === 'pending') return RED;
  if (s === 'in_progress') return AMBER;
  if (s === 'resolved' || s === 'completed' || s === 'closed') return GREEN;
  return TEXT_MID;
};

// ── Shared PDF builder helpers ────────────────────────────────────────────────
function createDoc() {
  return new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
}

function makeHelpers(doc, margin, contentW, pageW, pageH) {
  let currentPage = 1;
  let y = 0;
  const today = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const addPageHeader = (title, siteName, pageNum) => {
    doc?.setFillColor(...BRAND_DARK);
    doc?.rect(0, 0, pageW, 12, 'F');
    doc?.setFontSize(7);
    doc?.setTextColor(...WHITE);
    doc?.text('SiteProof – ' + title, margin, 8);
    doc?.text(`Page ${pageNum}`, pageW - margin, 8, { align: 'right' });
    doc?.text(siteName || 'All Sites', pageW / 2, 8, { align: 'center' });
  };

  const addPageFooter = () => {
    doc?.setFontSize(7);
    doc?.setTextColor(...TEXT_MID);
    doc?.text(`Generated by SiteProof on ${today}. All data sourced from live platform.`, margin, pageH - 6);
    doc?.text('CONFIDENTIAL', pageW - margin, pageH - 6, { align: 'right' });
  };

  const checkPageBreak = (neededHeight = 20, title, siteName) => {
    if (y + neededHeight > pageH - 20) {
      addPageFooter();
      doc?.addPage();
      currentPage++;
      addPageHeader(title, siteName, currentPage);
      y = 20;
    }
  };

  const addSectionTitle = (text) => {
    doc?.setFillColor(...BG_LIGHT);
    doc?.roundedRect(margin, y, contentW, 9, 1.5, 1.5, 'F');
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...BRAND_DARK);
    doc?.text(text, margin + 3, y + 6);
    y += 13;
  };

  const addKPIGrid = (items) => {
    const cols = Math.min(items?.length, 4);
    const boxW = (contentW - (cols - 1) * 3) / cols;
    items?.forEach((m, i) => {
      const bx = margin + i * (boxW + 3);
      doc?.setFillColor(...BG_LIGHT);
      doc?.roundedRect(bx, y, boxW, 22, 2, 2, 'F');
      doc?.setFillColor(...(m?.color || BRAND_ACCENT));
      doc?.roundedRect(bx, y, 3, 22, 1, 1, 'F');
      doc?.setFontSize(15);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...(m?.color || BRAND_ACCENT));
      doc?.text(String(m?.value ?? '—'), bx + 8, y + 14);
      doc?.setFontSize(7.5);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...TEXT_MID);
      doc?.text(m?.label, bx + 8, y + 20);
    });
    y += 28;
  };

  const addTable = (head, body, opts = {}) => {
    doc?.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [head],
      body,
      styles: { fontSize: 7.5, cellPadding: 2.5, textColor: TEXT_DARK },
      headStyles: { fillColor: opts?.headColor || BRAND_DARK, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: opts?.altColor || BG_LIGHT },
      columnStyles: opts?.columnStyles || {},
      didParseCell: opts?.didParseCell,
    });
    y = doc?.lastAutoTable?.finalY + 10;
  };

  const addSignatureBlock = (companyName) => {
    checkPageBreak(55, '', '');
    doc?.setFillColor(...BG_LIGHT);
    doc?.roundedRect(margin, y, contentW, 48, 2, 2, 'F');
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...TEXT_DARK);
    doc?.text('Authorisation & Signature', margin + 4, y + 8);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...TEXT_MID);
    doc?.text('This report has been generated from live SiteProof platform data.', margin + 4, y + 15);
    doc?.text(`Company: ${companyName || 'SiteProof'}`, margin + 4, y + 22);
    doc?.text(`Date: ${new Date()?.toLocaleDateString('en-GB')}`, margin + 4, y + 29);
    // Signature lines
    const sigY = y + 40;
    const col1 = margin + 4;
    const col2 = margin + contentW / 2 + 4;
    doc?.setDrawColor(...TEXT_MID);
    doc?.line(col1, sigY, col1 + 70, sigY);
    doc?.line(col2, sigY, col2 + 70, sigY);
    doc?.setFontSize(7);
    doc?.text('Authorised Signature', col1, sigY + 4);
    doc?.text('Name & Role', col2, sigY + 4);
    y += 54;
  };

  const addCoverPage = (reportTitle, siteName, companyName, fromLabel, toLabel) => {
    // Dark header band
    doc?.setFillColor(...BRAND_DARK);
    doc?.rect(0, 0, pageW, 70, 'F');
    doc?.setFillColor(...BRAND_ACCENT);
    doc?.rect(0, 68, pageW, 3, 'F');
    // Logo badge
    doc?.setFillColor(...BRAND_ACCENT);
    doc?.roundedRect(margin, 14, 14, 14, 2, 2, 'F');
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...WHITE);
    doc?.text('SP', margin + 3.5, 23.5);
    // Company name
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(203, 213, 225);
    doc?.text(companyName || 'SiteProof', margin + 18, 23);
    // Report title
    doc?.setFontSize(22);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...WHITE);
    doc?.text(reportTitle, margin, 48);
    // Site name
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(148, 163, 184);
    doc?.text(siteName || 'All Sites', margin, 57);
    // Meta
    y = 82;
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...TEXT_MID);
    doc?.text(`Report Period: ${fromLabel} – ${toLabel}`, margin, y);
    doc?.text(`Generated: ${today}`, pageW - margin, y, { align: 'right' });
    y += 10;
  };

  const startDataPage = (title, siteName) => {
    addPageFooter();
    doc?.addPage();
    currentPage++;
    addPageHeader(title, siteName, currentPage);
    y = 20;
  };

  const getY = () => y;
  const setY = (val) => { y = val; };
  const getPage = () => currentPage;
  const getToday = () => today;

  return {
    addPageHeader, addPageFooter, checkPageBreak,
    addSectionTitle, addKPIGrid, addTable, addSignatureBlock,
    addCoverPage, startDataPage,
    getY, setY, getPage, getToday,
  };
}

// ── PDF Export Service ────────────────────────────────────────────────────────
export const pdfExportService = {

  // ── 1. COMPLIANCE REPORT ──────────────────────────────────────────────────
  async generateComplianceReport({ reportData, siteName, companyName, dateFrom, dateTo, reportType, selectedModules }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;

    const today = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const fromLabel = dateFrom ? fmt(dateFrom) : 'All time';
    const toLabel   = dateTo   ? fmt(dateTo)   : today;

    const reportTypeLabels = {
      compliance: 'Compliance Report',
      progress: 'Project Progress Summary',
      audit: 'Audit Findings Report',
      audit_pack: 'Audit Pack',
      worker_performance: 'Worker Performance Analytics',
      custom: 'Custom Report',
    };
    const reportTitle = reportTypeLabels?.[reportType] || 'Compliance Report';

    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    // Cover page
    h?.addCoverPage(reportTitle, siteName, companyName, fromLabel, toLabel);

    // Site details box
    if (reportData?.site) {
      const site = reportData?.site;
      doc?.setFillColor(...BG_LIGHT);
      doc?.roundedRect(margin, h?.getY(), contentW, 36, 2, 2, 'F');
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...TEXT_DARK);
      doc?.text('Site Details', margin + 4, h?.getY() + 7);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...TEXT_MID);
      const details = [
        ['Site Name', site?.site_name || '—'],
        ['Reference', site?.site_reference || '—'],
        ['Address', [site?.address_line1, site?.city, site?.postcode]?.filter(Boolean)?.join(', ') || '—'],
        ['Client', site?.client_name || '—'],
        ['Status', site?.status || '—'],
        ['Start Date', fmt(site?.start_date)],
      ];
      let dx = margin + 4;
      let dy = h?.getY() + 14;
      details?.forEach(([label, value], i) => {
        if (i === 3) { dx = margin + contentW / 2 + 4; dy = h?.getY() + 14; }
        doc?.setFont('helvetica', 'bold');
        doc?.setTextColor(...TEXT_DARK);
        doc?.text(label + ':', dx, dy);
        doc?.setFont('helvetica', 'normal');
        doc?.setTextColor(...TEXT_MID);
        doc?.text(String(value), dx + 28, dy);
        dy += 7;
      });
      h?.setY(h?.getY() + 42);
    }

    // Summary KPI grid
    h?.setY(h?.getY() + 4);
    h?.addKPIGrid([
      { label: 'Tasks',     value: reportData?.tasks?.length     || 0, color: BRAND_ACCENT },
      { label: 'Incidents', value: reportData?.incidents?.length || 0, color: RED },
      { label: 'Snags',     value: reportData?.snags?.length     || 0, color: AMBER },
      { label: 'Documents', value: reportData?.documents?.length || 0, color: GREEN },
    ]);

    // Executive summary
    h?.addSectionTitle('Executive Summary');
    const summaryText = `This report covers the period ${fromLabel} to ${toLabel} for ${siteName || 'all sites'}. ` +
      `A total of ${reportData?.tasks?.length || 0} tasks, ${reportData?.incidents?.length || 0} incidents, ` +
      `${reportData?.snags?.length || 0} snags, and ${reportData?.documents?.length || 0} documents were recorded during this period.`;
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...TEXT_DARK);
    const summaryLines = doc?.splitTextToSize(summaryText, contentW - 6);
    doc?.text(summaryLines, margin + 3, h?.getY());
    h?.setY(h?.getY() + summaryLines?.length * 5 + 8);

    h?.addPageFooter();

    // ── Data pages ──────────────────────────────────────────────────────────
    h?.startDataPage(reportTitle, siteName);

    // Tasks
    if (selectedModules?.includes('tasks') && reportData?.tasks?.length > 0) {
      h?.addSectionTitle(`Tasks (${reportData?.tasks?.length})`);
      const completed = reportData?.tasks?.filter(t => t?.status === 'completed')?.length;
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...TEXT_MID);
      doc?.text(`Completed: ${completed}  |  Pending: ${reportData?.tasks?.length - completed}  |  Total: ${reportData?.tasks?.length}`, margin + 3, h?.getY());
      h?.setY(h?.getY() + 8);
      h?.addTable(
        ['Task', 'Category', 'Status', 'Assigned To', 'Work Carried Out By', 'Date', 'Notes'],
        reportData?.tasks?.slice(0, 100)?.map(t => [
          t?.title || '—',
          t?.category || '—',
          t?.status || '—',
          t?.assigned_user?.full_name || '—',
          t?.work_carried_out_by || '—',
          fmt(t?.date || t?.task_date),
          t?.notes ? t?.notes?.substring(0, 60) : '—',
        ]),
        {
          headColor: BRAND_DARK,
          columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 22 }, 2: { cellWidth: 20 }, 3: { cellWidth: 25 }, 4: { cellWidth: 28 }, 5: { cellWidth: 18 }, 6: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 2) {
              const s = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = s === 'completed' ? GREEN : s === 'pending' ? AMBER : RED;
            }
          },
        }
      );
    }

    // Incidents
    if (selectedModules?.includes('incidents') && reportData?.incidents?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Incidents (${reportData?.incidents?.length})`);
      h?.addTable(
        ['Type', 'Description', 'Severity', 'Location', 'Date', 'Reported By'],
        reportData?.incidents?.map(inc => [
          (inc?.incident_type || '—')?.replace(/_/g, ' '),
          (inc?.description || '—')?.substring(0, 80),
          inc?.severity_level || inc?.severity || '—',
          inc?.location_description || '—',
          fmt(inc?.incident_date),
          inc?.user_profiles?.full_name || '—',
        ]),
        {
          headColor: [127, 29, 29],
          altColor: [255, 251, 251],
          columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 55 }, 2: { cellWidth: 20 }, 3: { cellWidth: 30 }, 4: { cellWidth: 20 }, 5: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 2) {
              data.cell.styles.textColor = severityColor(data?.cell?.raw);
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // Snags
    if (selectedModules?.includes('snags') && reportData?.snags?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Snags (${reportData?.snags?.length})`);
      h?.addTable(
        ['Title', 'Description', 'Priority', 'Status', 'Location', 'Date', 'Raised By'],
        reportData?.snags?.map(snag => [
          snag?.title || '—',
          (snag?.description || '—')?.substring(0, 60),
          snag?.priority || '—',
          snag?.status || '—',
          snag?.location || '—',
          fmt(snag?.created_at),
          snag?.user_profiles?.full_name || '—',
        ]),
        {
          headColor: [120, 53, 15],
          altColor: [255, 253, 247],
          columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 45 }, 2: { cellWidth: 18 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 }, 5: { cellWidth: 18 }, 6: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 3) {
              data.cell.styles.textColor = statusColor(data?.cell?.raw);
              data.cell.styles.fontStyle = 'bold';
            }
            if (data?.section === 'body' && data?.column?.index === 2) {
              const p = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = (p === 'high' || p === 'critical') ? RED : p === 'medium' ? AMBER : GREEN;
            }
          },
        }
      );
    }

    // Documents
    if (selectedModules?.includes('documents') && reportData?.documents?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Documents (${reportData?.documents?.length})`);
      h?.addTable(
        ['Title', 'Category', 'Status', 'Expiry Date', 'Uploaded'],
        reportData?.documents?.map(d => [
          d?.title || d?.name || '—',
          d?.category || '—',
          d?.status || 'active',
          fmt(d?.expiry_date),
          fmt(d?.created_at),
        ]),
        {
          headColor: [30, 64, 175],
          altColor: [239, 246, 255],
          columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 35 }, 2: { cellWidth: 25 }, 3: { cellWidth: 28 }, 4: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 2) {
              const s = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = s?.includes('expir') ? AMBER : s?.includes('expired') ? RED : s?.includes('active') ? GREEN : TEXT_MID;
            }
          },
        }
      );
    }

    // Site Diary (daily_site_records). Sparse rows (nothing_to_report=true,
    // or every prose column null) collapse to a single "(No activity recorded)"
    // cell so the diary still serves as evidence of submission. See wqugEUH0.
    if (selectedModules?.includes('diary') && reportData?.diary?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Site Diary (${reportData?.diary?.length})`);
      const truncate = (s, n) => (s && s?.length > n ? s?.substring(0, n) + '…' : (s || '—'));
      const diaryRows = reportData?.diary?.map(d => {
        const proseCols = [d?.weather, d?.labour_on_site, d?.work_completed, d?.issues_delays];
        const allEmpty = proseCols?.every(c => !c || !String(c)?.trim());
        const submittedBy = d?.user_profiles?.full_name || '—';
        if (d?.nothing_to_report || allEmpty) {
          return [fmt(d?.record_date), '(No activity recorded)', '', '', '', submittedBy];
        }
        return [
          fmt(d?.record_date),
          truncate(d?.weather, 18),
          truncate(d?.labour_on_site, 30),
          truncate(d?.work_completed, 40),
          truncate(d?.issues_delays, 30),
          submittedBy,
        ];
      });
      h?.addTable(
        ['Date', 'Weather', 'Labour on Site', 'Work Completed', 'Issues/Delays', 'Submitted By'],
        diaryRows,
        {
          headColor: [13, 148, 136], // teal-600
          altColor: [240, 253, 250],
          columnStyles: { 0: { cellWidth: 20 }, 1: { cellWidth: 22 }, 2: { cellWidth: 32 }, 3: { cellWidth: 42 }, 4: { cellWidth: 32 }, 5: { cellWidth: 'auto' } },
        }
      );
    }

    // Near Misses — same data shape as incidents; reuses risk/status color
    // helpers. See wqugEUH0.
    if (selectedModules?.includes('near_misses') && reportData?.near_misses?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Near Misses (${reportData?.near_misses?.length})`);
      h?.addTable(
        ['Date', 'Location', 'Description', 'Risk Level', 'Status', 'Reported By'],
        reportData?.near_misses?.map(nm => [
          fmt(nm?.date),
          nm?.location || '—',
          (nm?.description || '—')?.substring(0, 60),
          nm?.risk_level || '—',
          nm?.status || '—',
          nm?.reporter?.full_name || '—',
        ]),
        {
          headColor: [194, 65, 12], // orange-700, between incidents red and snags amber
          altColor: [255, 247, 237],
          columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 28 }, 2: { cellWidth: 55 }, 3: { cellWidth: 22 }, 4: { cellWidth: 24 }, 5: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 3) {
              const r = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = r === 'high' ? RED : r === 'medium' ? AMBER : r === 'low' ? GREEN : TEXT_MID;
              data.cell.styles.fontStyle = 'bold';
            }
            if (data?.section === 'body' && data?.column?.index === 4) {
              const s = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = s === 'open' ? RED : s === 'under review' ? AMBER : s === 'closed' ? GREEN : TEXT_MID;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // RAMS — one row per document with "Acknowledged: X/Y" count. Counts are
    // populated by reportService via two batch queries. See wqugEUH0.
    if (selectedModules?.includes('rams') && reportData?.rams?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`H&S Docs (${reportData?.rams?.length})`);
      h?.addTable(
        ['Title', 'Uploaded', 'Uploaded By', 'Acknowledged'],
        reportData?.rams?.map(r => [
          r?.title || r?.file_name || '—',
          fmt(r?.created_at),
          r?.uploader?.full_name || '—',
          `${r?.ack_count ?? 0}/${r?.assign_count ?? 0}`,
        ]),
        {
          headColor: [67, 56, 202], // indigo-700
          altColor: [238, 242, 255],
          columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 26 }, 2: { cellWidth: 40 }, 3: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 3) {
              const raw = data?.cell?.raw || '0/0';
              const [x, y] = raw?.split('/')?.map(n => parseInt(n, 10));
              if (Number.isFinite(x) && Number.isFinite(y) && y > 0) {
                data.cell.styles.textColor = x >= y ? GREEN : x > 0 ? AMBER : RED;
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
        }
      );
    }

    // Photo Index — reference table only. Image embedding deferred to
    // fix/audit-pack-embed-photos. See wqugEUH0.
    if (selectedModules?.includes('photos') && reportData?.photos?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Photo Index (${reportData?.photos?.length})`);
      const sourceLabel = (s) => {
        if (!s) return '—';
        if (s === 'task') return 'Task';
        if (s === 'snag') return 'Snag';
        if (s === 'incident') return 'Incident';
        if (s === 'near_miss' || s === 'nearMiss') return 'Near Miss';
        return s;
      };
      h?.addTable(
        ['Caption / Filename', 'Source', 'Date', 'Uploaded By', 'Client Visible'],
        reportData?.photos?.map(p => [
          (p?.sourceTitle || p?.caption || p?.path || '—')?.toString()?.substring(0, 60),
          sourceLabel(p?.source),
          fmt(p?.date),
          p?.uploaded_by_name || '—',
          p?.client_visible === true ? 'Yes' : p?.client_visible === false ? 'No' : '—',
        ]),
        {
          headColor: [88, 28, 135], // purple-900
          altColor: [250, 245, 255],
          columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 22 }, 2: { cellWidth: 22 }, 3: { cellWidth: 40 }, 4: { cellWidth: 'auto' } },
        }
      );
    }

    // Audits
    if (selectedModules?.includes('audits') && reportData?.audits?.length > 0) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle(`Audits & Inspections (${reportData?.audits?.length})`);
      const auditPassed = reportData?.audits?.filter(a => a?.status === 'completed' || a?.status === 'passed')?.length;
      const auditFailed = reportData?.audits?.filter(a => a?.status === 'failed')?.length;
      h?.addKPIGrid([
        { label: 'Total Audits', value: reportData?.audits?.length, color: BRAND_ACCENT },
        { label: 'Completed', value: auditPassed, color: GREEN },
        { label: 'Failed', value: auditFailed, color: RED },
        { label: 'Pending', value: reportData?.audits?.length - auditPassed - auditFailed, color: AMBER },
      ]);
      h?.addTable(
        ['Audit Title', 'Type', 'Status', 'Score %', 'Date', 'Auditor'],
        reportData?.audits?.map(a => [
          a?.title || a?.audit_title || '—',
          (a?.audit_type || '—')?.replace(/_/g, ' '),
          (a?.status || '—')?.replace(/_/g, ' '),
          a?.score_percentage != null ? `${Math.round(a?.score_percentage)}%` : '—',
          fmt(a?.completed_at || a?.created_at),
          a?.user_profiles?.full_name || a?.auditor_name || '—',
        ]),
        {
          headColor: [55, 48, 163],
          altColor: [240, 253, 250],
          columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 30 }, 2: { cellWidth: 25 }, 3: { cellWidth: 18 }, 4: { cellWidth: 20 }, 5: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 2) {
              const s = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = s?.includes('fail') ? RED : s?.includes('complet') || s?.includes('pass') ? GREEN : AMBER;
              data.cell.styles.fontStyle = 'bold';
            }
            if (data?.section === 'body' && data?.column?.index === 3) {
              const raw = data?.cell?.raw;
              if (raw && raw !== '—') {
                const num = parseInt(raw);
                data.cell.styles.textColor = num >= 80 ? GREEN : num >= 60 ? AMBER : RED;
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
        }
      );
    }

    // Checklists / Inspections
    if (selectedModules?.includes('checklists')) {
      h?.checkPageBreak(20, reportTitle, siteName);
      h?.addSectionTitle('Checklists / Inspections');
      const checklists = reportData?.checklists || [];
      if (checklists?.length > 0) {
        const totalInspections = checklists?.length;
        const totalPassed = checklists?.reduce((sum, c) => sum + (c?.passed || 0), 0);
        const totalFailed = checklists?.reduce((sum, c) => sum + (c?.failed || 0), 0);
        const avgPassRate = totalInspections > 0
          ? Math.round(checklists?.reduce((sum, c) => sum + (c?.completion_pct || 0), 0) / totalInspections)
          : 0;
        h?.addKPIGrid([
          { label: 'Total Inspections', value: totalInspections, color: BRAND_ACCENT },
          { label: 'Avg Pass Rate', value: `${avgPassRate}%`, color: avgPassRate >= 80 ? GREEN : avgPassRate >= 60 ? AMBER : RED },
          { label: 'Total Pass Items', value: totalPassed, color: GREEN },
          { label: 'Total Fail Items', value: totalFailed, color: RED },
        ]);
        h?.addTable(
          ['Inspection Name', 'Date', 'Inspector', 'Pass', 'Fail', 'N/A', 'Score %', 'Notes', 'Photos'],
          checklists?.map(c => [
            c?.checklist_name || '—',
            fmt(c?.completed_at || c?.created_at),
            c?.inspector_name || '—',
            c?.passed ?? 0,
            c?.failed ?? 0,
            c?.na ?? 0,
            `${c?.completion_pct ?? 0}%`,
            c?.notes ? String(c?.notes)?.substring(0, 50) : '—',
            c?.has_photos ? 'Yes' : 'No',
          ]),
          {
            headColor: [13, 148, 136],
            altColor: [240, 253, 250],
            columnStyles: {
              0: { cellWidth: 38 },
              1: { cellWidth: 18 },
              2: { cellWidth: 28 },
              3: { cellWidth: 12 },
              4: { cellWidth: 12 },
              5: { cellWidth: 12 },
              6: { cellWidth: 16 },
              7: { cellWidth: 'auto' },
              8: { cellWidth: 14 },
            },
            didParseCell: (data) => {
              if (data?.section === 'body' && data?.column?.index === 6) {
                const raw = data?.cell?.raw;
                if (raw && raw !== '—') {
                  const num = parseInt(raw);
                  data.cell.styles.textColor = num >= 80 ? GREEN : num >= 60 ? AMBER : RED;
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            },
          }
        );
      } else {
        doc?.setFontSize(9);
        doc?.setFont('helvetica', 'normal');
        doc?.setTextColor(...TEXT_MID);
        doc?.text('No inspection records found for this period.', margin + 3, h?.getY());
        h?.setY(h?.getY() + 10);
      }
    }

    // Signature block
    h?.checkPageBreak(60, reportTitle, siteName);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const siteSlug = (siteName || 'all-sites')?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-${reportType || 'compliance'}-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 2. SNAG REPORT ────────────────────────────────────────────────────────
  async generateSnagReport({ snags, siteName, companyName, filters }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);
    const today = h?.getToday();

    const siteLabel = siteName || filters?.siteName || 'All Sites';
    const dateLabel = new Date()?.toLocaleDateString('en-GB');

    // Cover
    h?.addCoverPage('Snag Report', siteLabel, companyName, 'All time', dateLabel);

    // Summary KPIs
    const open       = snags?.filter(s => s?.status === 'open')?.length;
    const inProgress = snags?.filter(s => s?.status === 'in_progress')?.length;
    const resolved   = snags?.filter(s => s?.status === 'resolved')?.length;
    const high       = snags?.filter(s => s?.priority === 'high' || s?.priority === 'critical')?.length;

    h?.addKPIGrid([
      { label: 'Total Snags',  value: snags?.length, color: BRAND_ACCENT },
      { label: 'Open',         value: open,          color: RED },
      { label: 'In Progress',  value: inProgress,    color: AMBER },
      { label: 'Resolved',     value: resolved,      color: GREEN },
    ]);

    // Counts by priority
    h?.addSectionTitle('Summary by Priority');
    const priorities = ['critical', 'high', 'medium', 'low'];
    h?.addTable(
      ['Priority', 'Count', 'Open', 'In Progress', 'Resolved'],
      priorities?.map(p => {
        const pSnags = snags?.filter(s => s?.priority === p);
        return [
          p?.charAt(0)?.toUpperCase() + p?.slice(1),
          pSnags?.length,
          pSnags?.filter(s => s?.status === 'open')?.length,
          pSnags?.filter(s => s?.status === 'in_progress')?.length,
          pSnags?.filter(s => s?.status === 'resolved')?.length,
        ];
      })?.filter(row => row?.[1] > 0),
      {
        headColor: [120, 53, 15],
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 0) {
            const p = data?.cell?.raw?.toLowerCase();
            data.cell.styles.textColor = (p === 'critical' || p === 'high') ? RED : p === 'medium' ? AMBER : GREEN;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      }
    );

    // Counts by status
    h?.addSectionTitle('Summary by Status');
    const statuses = ['open', 'in_progress', 'resolved', 'closed'];
    h?.addTable(
      ['Status', 'Count', '% of Total'],
      statuses?.map(s => {
        const cnt = snags?.filter(sn => sn?.status === s)?.length;
        return [s?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase()), cnt, pct(cnt, snags?.length)];
      })?.filter(row => row?.[1] > 0),
      { headColor: BRAND_DARK }
    );

    // Counts by category
    const categories = [...new Set(snags?.map(s => s?.category)?.filter(Boolean))];
    if (categories?.length > 0) {
      h?.addSectionTitle('Summary by Category');
      h?.addTable(
        ['Category', 'Count', 'Open', 'Resolved'],
        categories?.map(cat => {
          const cSnags = snags?.filter(s => s?.category === cat);
          return [
            cat,
            cSnags?.length,
            cSnags?.filter(s => s?.status === 'open')?.length,
            cSnags?.filter(s => s?.status === 'resolved')?.length,
          ];
        }),
        { headColor: BRAND_DARK }
      );
    }

    h?.addPageFooter();
    h?.startDataPage('Snag Report', siteLabel);

    // Full snag list
    h?.addSectionTitle(`All Snags (${snags?.length})`);
    h?.addTable(
      ['ID', 'Title', 'Location', 'Priority', 'Status', 'Created', 'Assigned To', 'Due Date'],
      snags?.map(s => [
        s?.id?.substring(0, 8) || '—',
        (s?.title || '—')?.substring(0, 40),
        s?.location || '—',
        s?.priority || '—',
        (s?.status || '—')?.replace(/_/g, ' '),
        fmt(s?.created_at),
        s?.assigned_user?.full_name || '—',
        fmt(s?.due_date),
      ]),
      {
        headColor: [120, 53, 15],
        altColor: [255, 253, 247],
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 40 },
          2: { cellWidth: 25 },
          3: { cellWidth: 18 },
          4: { cellWidth: 20 },
          5: { cellWidth: 18 },
          6: { cellWidth: 25 },
          7: { cellWidth: 'auto' },
        },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 3) {
            const p = data?.cell?.raw?.toLowerCase();
            data.cell.styles.textColor = (p === 'high' || p === 'critical') ? RED : p === 'medium' ? AMBER : GREEN;
            data.cell.styles.fontStyle = 'bold';
          }
          if (data?.section === 'body' && data?.column?.index === 4) {
            data.cell.styles.textColor = statusColor(data?.cell?.raw);
          }
        },
      }
    );

    // Photo references
    const snagsWithPhotos = snags?.filter(s => s?.photo_paths?.length > 0);
    if (snagsWithPhotos?.length > 0) {
      h?.checkPageBreak(20, 'Snag Report', siteLabel);
      h?.addSectionTitle(`Photo Evidence (${snagsWithPhotos?.length} snags with photos)`);
      h?.addTable(
        ['Snag Title', 'Location', 'Photo Count', 'Status'],
        snagsWithPhotos?.map(s => [
          s?.title || '—',
          s?.location || '—',
          s?.photo_paths?.length || 0,
          (s?.status || '—')?.replace(/_/g, ' '),
        ]),
        { headColor: [88, 28, 135], altColor: [250, 245, 255] }
      );
    }

    // Signature
    h?.checkPageBreak(60, 'Snag Report', siteLabel);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const siteSlug = siteLabel?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-snag-report-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 3. TASK ANALYTICS REPORT ──────────────────────────────────────────────
  async generateTaskAnalyticsReport({ kpis, workerStats, siteStats, allTasks, siteName, companyName, dateRange }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);
    const today = h?.getToday();

    const siteLabel = siteName || 'All Sites';
    const periodLabel = dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 30 Days' : 'Last 14 Days';

    // Cover
    h?.addCoverPage('Task Analytics Report', siteLabel, companyName, periodLabel, today);

    // KPI grid
    h?.addKPIGrid([
      { label: 'Total Tasks',    value: kpis?.total     || 0, color: BRAND_ACCENT },
      { label: 'Completed',      value: kpis?.completed || 0, color: GREEN },
      { label: 'Overdue',        value: kpis?.overdue   || 0, color: RED },
      { label: 'Blocked',        value: kpis?.blocked   || 0, color: AMBER },
    ]);

    // Completion rate summary
    h?.addSectionTitle('Completion Summary');
    h?.addTable(
      ['Metric', 'Value'],
      [
        ['Overall Completion Rate', `${kpis?.rate || 0}%`],
        ['Required Tasks Rate',     `${kpis?.requiredRate || 0}%`],
        ['Total Tasks Today',       String(kpis?.total || 0)],
        ['Completed Tasks',         String(kpis?.completed || 0)],
        ['Overdue Tasks',           String(kpis?.overdue || 0)],
        ['Blocked Tasks',           String(kpis?.blocked || 0)],
      ],
      {
        headColor: BRAND_DARK,
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 'auto' } },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 1) {
            const val = data?.cell?.raw;
            if (typeof val === 'string' && val?.includes('%')) {
              const num = parseInt(val);
              data.cell.styles.textColor = num >= 80 ? GREEN : num >= 50 ? AMBER : RED;
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
      }
    );

    h?.addPageFooter();
    h?.startDataPage('Task Analytics Report', siteLabel);

    // Worker performance table
    if (workerStats?.length > 0) {
      h?.addSectionTitle('Worker Performance');
      h?.addTable(
        ['Worker', 'Total Tasks', 'Completed', 'Pending', 'Blocked', 'Completion Rate'],
        workerStats?.map(w => [
          w?.full_name || '—',
          w?.tasks_total || 0,
          w?.tasks_completed || 0,
          w?.tasks_pending || 0,
          w?.tasks_blocked || 0,
          pct(w?.tasks_completed || 0, w?.tasks_total || 0),
        ]),
        {
          headColor: BRAND_DARK,
          columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 22 }, 2: { cellWidth: 22 }, 3: { cellWidth: 22 }, 4: { cellWidth: 22 }, 5: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 5) {
              const num = parseInt(data?.cell?.raw);
              data.cell.styles.textColor = num >= 80 ? GREEN : num >= 50 ? AMBER : RED;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // Site stats table
    if (siteStats?.length > 0) {
      h?.checkPageBreak(20, 'Task Analytics Report', siteLabel);
      h?.addSectionTitle('Site Performance');
      h?.addTable(
        ['Site', 'Total Tasks', 'Completed', 'Overdue', 'Completion Rate'],
        siteStats?.map(s => [
          s?.site_name || '—',
          s?.tasks_total || 0,
          s?.tasks_completed || 0,
          s?.tasks_overdue || 0,
          pct(s?.tasks_completed || 0, s?.tasks_total || 0),
        ]),
        {
          headColor: BRAND_DARK,
          columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 25 }, 2: { cellWidth: 25 }, 3: { cellWidth: 25 }, 4: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 4) {
              const num = parseInt(data?.cell?.raw);
              data.cell.styles.textColor = num >= 80 ? GREEN : num >= 50 ? AMBER : RED;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // Category breakdown from allTasks
    if (allTasks?.length > 0) {
      h?.checkPageBreak(20, 'Task Analytics Report', siteLabel);
      h?.addSectionTitle('Category Breakdown');
      const cats = [...new Set(allTasks?.map(t => t?.category)?.filter(Boolean))];
      h?.addTable(
        ['Category', 'Total', 'Completed', 'Pending', 'Completion Rate'],
        cats?.map(cat => {
          const cTasks = allTasks?.filter(t => t?.category === cat);
          const done = cTasks?.filter(t => t?.status === 'completed')?.length;
          return [cat, cTasks?.length, done, cTasks?.length - done, pct(done, cTasks?.length)];
        }),
        {
          headColor: BRAND_DARK,
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 4) {
              const num = parseInt(data?.cell?.raw);
              data.cell.styles.textColor = num >= 80 ? GREEN : num >= 50 ? AMBER : RED;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // Signature
    h?.checkPageBreak(60, 'Task Analytics Report', siteLabel);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const siteSlug = siteLabel?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-task-analytics-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 4. BUDGET REPORT ──────────────────────────────────────────────────────
  async generateBudgetReport({ categories, totalBudget, totalActual, totalCommitted, remaining, companyName, siteName, period }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);
    const today = h?.getToday();

    const siteLabel = siteName || 'All Sites';
    const periodLabel = period || 'All Time';
    const spentPct = totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0;
    const overBudgetItems = categories?.filter(c => c?.actual > c?.budget);

    // Cover
    h?.addCoverPage('Budget Report', siteLabel, companyName, periodLabel, today);

    // KPI grid
    h?.addKPIGrid([
      { label: 'Total Budget',  value: currency(totalBudget),   color: BRAND_ACCENT },
      { label: 'Actual Spend',  value: currency(totalActual),   color: spentPct > 90 ? RED : spentPct > 70 ? AMBER : GREEN },
      { label: 'Committed (Pending)',     value: currency(totalCommitted), color: AMBER },
      { label: 'Remaining Available',     value: currency(remaining),     color: remaining < 0 ? RED : GREEN },
    ]);

    // Budget utilisation summary
    h?.addSectionTitle('Budget Utilisation Summary');
    h?.addTable(
      ['Metric', 'Value', 'Notes'],
      [
        ['Total Budget',          currency(totalBudget),    'Allocated project budget'],
        ['Actual Spend',          currency(totalActual),    `${spentPct}% of total budget`],
        ['Committed (Pending)',   currency(totalCommitted), 'Approved but not yet spent'],
        ['Remaining Available',   currency(remaining),      remaining < 0 ? 'OVER BUDGET' : 'Available to allocate'],
        ['Over-budget Categories', String(overBudgetItems?.length), overBudgetItems?.map(c => c?.name || c?.category)?.join(', ') || 'None'],
      ],
      {
        headColor: BRAND_DARK,
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 35 }, 2: { cellWidth: 'auto' } },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 1) {
            const raw = String(data?.cell?.raw || '');
            if (raw?.startsWith('-') || (data?.row?.index === 3 && remaining < 0)) {
              data.cell.styles.textColor = RED;
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
      }
    );

    h?.addPageFooter();
    h?.startDataPage('Budget Report', siteLabel);

    // Category breakdown
    if (categories?.length > 0) {
      h?.addSectionTitle('Category Breakdown');
      h?.addTable(
        ['Category', 'Budget', 'Actual Spend', 'Committed', 'Remaining', 'Variance'],
        categories?.map(c => {
          const variance = (c?.budget || 0) - (c?.actual || 0);
          return [
            c?.name || c?.category || '—',
            currency(c?.budget),
            currency(c?.actual),
            currency(c?.committed || 0),
            currency((c?.budget || 0) - (c?.actual || 0) - (c?.committed || 0)),
            (variance >= 0 ? '+' : '') + currency(variance),
          ];
        }),
        {
          headColor: BRAND_DARK,
          columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 25 }, 2: { cellWidth: 25 }, 3: { cellWidth: 25 }, 4: { cellWidth: 25 }, 5: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 5) {
              const raw = String(data?.cell?.raw || '');
              data.cell.styles.textColor = raw?.startsWith('-') ? RED : GREEN;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // Variance table (over-budget items)
    if (overBudgetItems?.length > 0) {
      h?.checkPageBreak(20, 'Budget Report', siteLabel);
      h?.addSectionTitle(`Over-Budget Categories (${overBudgetItems?.length})`);
      h?.addTable(
        ['Category', 'Budget', 'Actual', 'Overspend', 'Overspend %'],
        overBudgetItems?.map(c => {
          const overspend = (c?.actual || 0) - (c?.budget || 0);
          return [
            c?.name || c?.category || '—',
            currency(c?.budget),
            currency(c?.actual),
            currency(overspend),
            pct(overspend, c?.budget),
          ];
        }),
        {
          headColor: [127, 29, 29],
          altColor: [255, 251, 251],
          didParseCell: (data) => {
            if (data?.section === 'body' && (data?.column?.index === 3 || data?.column?.index === 4)) {
              data.cell.styles.textColor = RED;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    }

    // Signature
    h?.checkPageBreak(60, 'Budget Report', siteLabel);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const siteSlug = siteLabel?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-budget-report-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 5. INCIDENT REPORT ───────────────────────────────────────────────────
  async generateIncidentReport({ incidents, siteName, companyName, dateFrom, dateTo }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const today = new Date()?.toLocaleDateString('en-GB');
    const fromLabel = dateFrom ? fmt(dateFrom) : 'All time';
    const toLabel   = dateTo   ? fmt(dateTo)   : today;
    const siteLabel = siteName || 'All Sites';
    const incList   = incidents || [];

    // Cover page
    h?.addCoverPage('Incident Report', siteLabel, companyName, fromLabel, toLabel);

    // KPI grid
    const open         = incList?.filter(i => i?.status === 'open')?.length;
    const investigating = incList?.filter(i => i?.status === 'investigating' || i?.status === 'in_progress')?.length;
    const resolved     = incList?.filter(i => i?.status === 'resolved' || i?.status === 'closed')?.length;
    const critical     = incList?.filter(i => (i?.severity_level || i?.severity)?.toLowerCase() === 'critical')?.length;

    h?.addKPIGrid([
      { label: 'Total Incidents',  value: incList?.length, color: BRAND_ACCENT },
      { label: 'Open',             value: open,            color: RED },
      { label: 'Investigating',    value: investigating,   color: AMBER },
      { label: 'Resolved',         value: resolved,        color: GREEN },
    ]);

    // Summary by type
    h?.addSectionTitle('Summary by Incident Type');
    const incidentTypes = ['near_miss', 'injury', 'property_damage', 'unsafe_condition', 'other'];
    const typeLabels = {
      near_miss: 'Near Miss',
      injury: 'Injury',
      property_damage: 'Property Damage',
      unsafe_condition: 'Unsafe Condition',
      other: 'Other',
    };
    h?.addTable(
      ['Incident Type', 'Count', 'Open', 'Investigating', 'Resolved'],
      incidentTypes?.map(t => {
        const tIncs = incList?.filter(i => i?.incident_type === t);
        return [
          typeLabels?.[t] || t,
          tIncs?.length,
          tIncs?.filter(i => i?.status === 'open')?.length,
          tIncs?.filter(i => i?.status === 'investigating' || i?.status === 'in_progress')?.length,
          tIncs?.filter(i => i?.status === 'resolved' || i?.status === 'closed')?.length,
        ];
      })?.filter(row => row?.[1] > 0),
      { headColor: [127, 29, 29], altColor: [255, 251, 251] }
    );

    // Critical severity count note
    if (critical > 0) {
      doc?.setFontSize(8.5);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...RED);
      doc?.text(`⚠  Critical Severity Incidents: ${critical}`, margin + 3, h?.getY());
      h?.setY(h?.getY() + 8);
    }

    h?.addPageFooter();

    // Detail page
    h?.startDataPage('Incident Report', siteLabel);
    h?.addSectionTitle(`Incident Detail (${incList?.length})`);
    h?.addTable(
      ['Date', 'Type', 'Severity', 'Location', 'Description', 'Reported By', 'Status'],
      incList?.map(i => [
        fmt(i?.incident_date),
        (typeLabels?.[i?.incident_type] || i?.incident_type || '—'),
        i?.severity_level || i?.severity || '—',
        i?.location_description || '—',
        (i?.description || '—')?.substring(0, 60),
        i?.user_profiles?.full_name || i?.reported_by || '—',
        (i?.status || '—')?.replace(/_/g, ' '),
      ]),
      {
        headColor: [127, 29, 29],
        altColor: [255, 251, 251],
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 28 },
          2: { cellWidth: 20 },
          3: { cellWidth: 28 },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 28 },
          6: { cellWidth: 22 },
        },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 2) {
            data.cell.styles.textColor = severityColor(data?.cell?.raw);
            data.cell.styles.fontStyle = 'bold';
          }
          if (data?.section === 'body' && data?.column?.index === 6) {
            data.cell.styles.textColor = statusColor(data?.cell?.raw);
            data.cell.styles.fontStyle = 'bold';
          }
        },
      }
    );

    h?.checkPageBreak(60, 'Incident Report', siteLabel);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const siteSlug = siteLabel?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-incident-report-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 6. TOOLBOX TALK ATTENDANCE ───────────────────────────────────────────
  async generateToolboxTalkAttendance({ talk, assignments, confirmations, siteName, companyName }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const today = new Date()?.toLocaleDateString('en-GB');
    const siteLabel = siteName || 'All Sites';
    const talkTitle = talk?.title || 'Toolbox Talk';
    const rows = assignments || [];
    const confirmed = rows?.filter(a => a?.confirmed);
    const pending   = rows?.filter(a => !a?.confirmed);
    const completionPct = rows?.length > 0 ? Math.round((confirmed?.length / rows?.length) * 100) : 0;

    // Cover page — use addCoverPage then add subtitle
    h?.addCoverPage('Toolbox Talk Attendance Record', siteLabel, companyName, 'Attendance Record', today);

    // Talk title subtitle
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...BRAND_ACCENT);
    doc?.text(`Talk: ${talkTitle}`, margin, h?.getY());
    h?.setY(h?.getY() + 10);

    // KPI grid
    h?.addKPIGrid([
      { label: 'Total Assigned',       value: rows?.length,        color: BRAND_ACCENT },
      { label: 'Confirmed Attendance', value: confirmed?.length,   color: GREEN },
      { label: 'Pending',              value: pending?.length,     color: RED },
      { label: 'Completion %',         value: completionPct + '%', color: completionPct >= 80 ? GREEN : completionPct >= 50 ? AMBER : RED },
    ]);

    h?.addPageFooter();

    // Attendance detail page
    h?.startDataPage('Toolbox Talk Attendance Record', siteLabel);
    h?.addSectionTitle(`Attendance List — ${talkTitle}`);
    h?.addTable(
      ['Worker Name', 'Date Assigned', 'Confirmed', 'Confirmation Date/Time'],
      rows?.map(a => [
        a?.worker?.full_name || a?.user_profiles?.full_name || '—',
        fmt(a?.assigned_at || a?.created_at),
        a?.confirmed ? 'Confirmed' : 'Pending',
        a?.confirmed_at ? new Date(a?.confirmed_at)?.toLocaleString('en-GB') : '—',
      ]),
      {
        headColor: BRAND_DARK,
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 35 },
          2: { cellWidth: 30 },
          3: { cellWidth: 'auto' },
        },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 2) {
            const val = data?.cell?.raw?.toLowerCase();
            data.cell.styles.textColor = val === 'confirmed' ? GREEN : RED;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      }
    );

    h?.checkPageBreak(60, 'Toolbox Talk Attendance Record', siteLabel);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const talkSlug = talkTitle?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-toolbox-attendance-${talkSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 7. RAMS ACKNOWLEDGEMENT ──────────────────────────────────────────────
  async generateRamsAcknowledgement({ ramsDoc, assignments, acknowledgements, siteName, companyName }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const today = new Date()?.toLocaleDateString('en-GB');
    const siteLabel = siteName || 'All Sites';
    const docTitle  = ramsDoc?.title || 'H&S Document';
    const rows = assignments || [];
    const acknowledged = rows?.filter(a => a?.acknowledged);
    const pending      = rows?.filter(a => !a?.acknowledged);
    const ackPct = rows?.length > 0 ? Math.round((acknowledged?.length / rows?.length) * 100) : 0;

    // Cover page
    h?.addCoverPage('H&S Document Acknowledgement Record', siteLabel, companyName, 'Acknowledgement Record', today);

    // Document title subtitle
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...BRAND_ACCENT);
    doc?.text(`Document: ${docTitle}`, margin, h?.getY());
    h?.setY(h?.getY() + 10);

    // KPI grid
    h?.addKPIGrid([
      { label: 'Total Assigned',       value: rows?.length,          color: BRAND_ACCENT },
      { label: 'Acknowledged',         value: acknowledged?.length,  color: GREEN },
      { label: 'Pending',              value: pending?.length,       color: RED },
      { label: 'Acknowledgement %',    value: ackPct + '%',          color: ackPct >= 80 ? GREEN : ackPct >= 50 ? AMBER : RED },
    ]);

    h?.addPageFooter();

    // Acknowledgement detail page
    h?.startDataPage('H&S Document Acknowledgement Record', siteLabel);
    h?.addSectionTitle(`Acknowledgement List — ${docTitle}`);
    h?.addTable(
      ['Worker Name', 'Date Assigned', 'Acknowledged', 'Acknowledgement Date/Time'],
      rows?.map(a => [
        a?.worker?.full_name || a?.user_profiles?.full_name || '—',
        fmt(a?.assigned_at || a?.created_at),
        a?.acknowledged ? 'Acknowledged' : 'Pending',
        a?.acknowledged_at ? new Date(a?.acknowledged_at)?.toLocaleString('en-GB') : '—',
      ]),
      {
        headColor: BRAND_DARK,
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
          3: { cellWidth: 'auto' },
        },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 2) {
            const val = data?.cell?.raw?.toLowerCase();
            data.cell.styles.textColor = val === 'acknowledged' ? GREEN : RED;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      }
    );

    h?.checkPageBreak(60, 'H&S Document Acknowledgement Record', siteLabel);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const docSlug  = docTitle?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-rams-acknowledgement-${docSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── 8. WORKER CERTIFICATIONS REPORT ─────────────────────────────────────
  async generateWorkerCertificationsReport({ certifications, siteName, companyName, workerName, filterStatus }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const today = new Date()?.toLocaleDateString('en-GB');
    const certList = certifications || [];

    const getCertStatus = (expiryDate) => {
      if (!expiryDate) return 'unknown';
      const now = new Date();
      const exp = new Date(expiryDate);
      const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'expired';
      if (diffDays <= 30) return 'expiring';
      return 'valid';
    };

    const valid    = certList?.filter(c => getCertStatus(c?.expiry_date) === 'valid')?.length;
    const expiring = certList?.filter(c => getCertStatus(c?.expiry_date) === 'expiring')?.length;
    const expired  = certList?.filter(c => getCertStatus(c?.expiry_date) === 'expired')?.length;

    const coverSubtitle = workerName ? `Worker: ${workerName}` : companyName || 'All Workers';

    // Cover page
    h?.addCoverPage('Worker Certifications Report', coverSubtitle, companyName, 'All Certifications', today);

    // KPI grid
    h?.addKPIGrid([
      { label: 'Total Certifications', value: certList?.length, color: BRAND_ACCENT },
      { label: 'Valid',                value: valid,            color: GREEN },
      { label: 'Expiring (30d)',       value: expiring,         color: AMBER },
      { label: 'Expired',              value: expired,          color: RED },
    ]);

    h?.addPageFooter();

    // Detail page
    h?.startDataPage('Worker Certifications Report', coverSubtitle);

    if (workerName) {
      // Single worker — flat table
      h?.addSectionTitle(`Certifications — ${workerName}`);
      h?.addTable(
        ['Worker Name', 'Certificate Type', 'Certificate Number', 'Expiry Date', 'Status'],
        certList?.map(c => [
          c?.worker?.full_name || workerName || '—',
          c?.certificate_type || '—',
          c?.certificate_number || '—',
          fmt(c?.expiry_date),
          getCertStatus(c?.expiry_date)?.charAt(0)?.toUpperCase() + getCertStatus(c?.expiry_date)?.slice(1),
        ]),
        {
          headColor: BRAND_DARK,
          columnStyles: {
            0: { cellWidth: 45 },
            1: { cellWidth: 35 },
            2: { cellWidth: 35 },
            3: { cellWidth: 25 },
            4: { cellWidth: 'auto' },
          },
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 4) {
              const val = data?.cell?.raw?.toLowerCase();
              data.cell.styles.textColor = val === 'valid' ? GREEN : val === 'expiring' ? AMBER : RED;
              data.cell.styles.fontStyle = 'bold';
            }
          },
        }
      );
    } else {
      // Group by worker
      const workerMap = {};
      certList?.forEach(c => {
        const wName = c?.worker?.full_name || 'Unknown Worker';
        if (!workerMap?.[wName]) workerMap[wName] = [];
        workerMap?.[wName]?.push(c);
      });

      Object?.entries(workerMap)?.forEach(([wName, wCerts]) => {
        h?.checkPageBreak(20, 'Worker Certifications Report', coverSubtitle);
        h?.addSectionTitle(`${wName} (${wCerts?.length} certification${wCerts?.length !== 1 ? 's' : ''})`);
        h?.addTable(
          ['Worker Name', 'Certificate Type', 'Certificate Number', 'Expiry Date', 'Status'],
          wCerts?.map(c => [
            wName,
            c?.certificate_type || '—',
            c?.certificate_number || '—',
            fmt(c?.expiry_date),
            getCertStatus(c?.expiry_date)?.charAt(0)?.toUpperCase() + getCertStatus(c?.expiry_date)?.slice(1),
          ]),
          {
            headColor: BRAND_DARK,
            columnStyles: {
              0: { cellWidth: 45 },
              1: { cellWidth: 35 },
              2: { cellWidth: 35 },
              3: { cellWidth: 25 },
              4: { cellWidth: 'auto' },
            },
            didParseCell: (data) => {
              if (data?.section === 'body' && data?.column?.index === 4) {
                const val = data?.cell?.raw?.toLowerCase();
                data.cell.styles.textColor = val === 'valid' ? GREEN : val === 'expiring' ? AMBER : RED;
                data.cell.styles.fontStyle = 'bold';
              }
            },
          }
        );
      });
    }

    h?.checkPageBreak(60, 'Worker Certifications Report', coverSubtitle);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const workerSlug = workerName ? workerName?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '') + '-' : '';
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];
    const filename = `siteproof-worker-certifications-${workerSlug}${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  async generateChecklistCompletionReport({ completion, items, responses, siteName, checklistName, companyName, completedBy, completedAt }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const dateLabel = fmt(completedAt || completion?.completion_date);
    const siteSlug = (siteName || 'site')?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];

    // Cover page
    h?.addCoverPage('Site Inspection Report', siteName || 'Unknown Site', companyName || 'SiteProof', dateLabel, dateLabel);

    // Subtitle: checklist name
    doc?.setFontSize(13);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...BRAND_DARK);
    const h_obj = makeHelpers(doc, margin, contentW, pageW, pageH);
    doc?.text(checklistName || completion?.site_checklists?.checklist_name || 'Inspection', margin, 74);

    // KPI grid
    const allResponses = responses || completion?.checklist_item_responses || [];
    const passCount = allResponses?.filter(r => r?.status === 'pass')?.length || 0;
    const failCount = allResponses?.filter(r => r?.status === 'fail')?.length || 0;
    const naCount = allResponses?.filter(r => r?.status === 'na')?.length || 0;
    const totalCount = allResponses?.length || 0;
    const completionPct = totalCount > 0 ? Math.round((passCount / totalCount) * 100) : 0;

    h?.addKPIGrid([
      { label: 'Total Items', value: totalCount, color: BRAND_ACCENT },
      { label: 'Passed', value: passCount, color: GREEN },
      { label: 'Failed', value: failCount, color: RED },
      { label: 'N/A', value: naCount, color: TEXT_MID },
    ]);

    // Completion % box
    doc?.setFillColor(...BG_LIGHT);
    doc?.roundedRect(margin, h?.y !== undefined ? h?.y : 130, contentW, 12, 2, 2, 'F');
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...(completionPct >= 80 ? GREEN : completionPct >= 50 ? AMBER : RED));
    const yPos = (typeof h?.getY === 'function' ? h?.getY() : 130);

    h?.addSectionTitle(`Completion: ${completionPct}% Pass Rate`);

    // Summary table
    h?.addSectionTitle('Inspection Item Responses');

    const itemMap = {};
    (items || [])?.forEach(item => { itemMap[item?.id] = item; });

    const tableRows = allResponses?.map(r => {
      const itemDesc = itemMap?.[r?.checklist_item_id]?.item_description || r?.checklist_item_id || '—';
      const statusLabel = r?.status === 'pass' ? 'Pass' : r?.status === 'fail' ? 'Fail' : '—';
      const hasPhoto = r?.photo_path ? 'Yes' : 'No';
      return [itemDesc, statusLabel, r?.comment || '—', hasPhoto];
    });

    h?.addTable(
      ['Item Description', 'Response', 'Notes / Comment', 'Photo'],
      tableRows?.length > 0 ? tableRows : [['No responses recorded', '—', '—', '—']],
      {
        headColor: BRAND_DARK,
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 22 },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 18 },
        },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 1) {
            const val = data?.cell?.raw?.toLowerCase();
            data.cell.styles.textColor = val === 'pass' ? GREEN : val === 'fail' ? RED : TEXT_MID;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      }
    );

    // Signature block
    h?.checkPageBreak(60, 'Site Inspection Report', siteName);
    doc?.setFillColor(...BG_LIGHT);
    doc?.roundedRect(margin, doc?.lastAutoTable?.finalY + 10 || 200, contentW, 48, 2, 2, 'F');
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const filename = `siteproof-inspection-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  async generateAuditReport({ audit, questions, responses, siteName, companyName, auditorName, completedAt }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const auditName = audit?.audit_name || 'Audit';
    const auditType = audit?.template_type || audit?.audit_type || 'General';
    const dateLabel = fmt(completedAt || audit?.audit_date);
    const siteSlug = (siteName || 'site')?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];

    // Cover page
    h?.addCoverPage('Audit Report', siteName || 'Unknown Site', companyName || 'SiteProof', dateLabel, dateLabel);

    // Subtitle: audit name + type
    doc?.setFontSize(13);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...BRAND_DARK);
    doc?.text(`${auditName} — ${auditType}`, margin, 74);

    // KPI grid
    const allResponses = responses || [];
    const passCount = allResponses?.filter(r => r?.pass === true)?.length || 0;
    const failCount = allResponses?.filter(r => r?.pass === false)?.length || 0;
    const totalQ = allResponses?.length || 0;
    const scoreRaw = totalQ > 0 ? Math.round((passCount / totalQ) * 100) : 0;
    const scoreColor = scoreRaw >= 80 ? GREEN : scoreRaw >= 60 ? AMBER : RED;

    h?.addKPIGrid([
      { label: 'Total Questions', value: totalQ, color: BRAND_ACCENT },
      { label: 'Passed', value: passCount, color: GREEN },
      { label: 'Failed', value: failCount, color: RED },
      { label: 'Score %', value: `${scoreRaw}%`, color: scoreColor },
    ]);

    // Questions table
    h?.addSectionTitle('Audit Questions & Responses');

    const questionMap = {};
    (questions || [])?.forEach(q => { questionMap[q?.id] = q; });

    const tableRows = allResponses?.map((r, idx) => {
      const questionText = questionMap?.[r?.question_id]?.question_text ||
        r?.audit_questions?.question_text || `Question ${idx + 1}`;
      const responseLabel = r?.pass === true ? 'Pass' : r?.pass === false ? 'Fail' : '—';
      return [String(idx + 1), questionText, responseLabel, r?.comment || '—'];
    });

    h?.addTable(
      ['#', 'Question', 'Response', 'Comments'],
      tableRows?.length > 0 ? tableRows : [['—', 'No questions recorded', '—', '—']],
      {
        headColor: BRAND_DARK,
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 90 },
          2: { cellWidth: 22 },
          3: { cellWidth: 'auto' },
        },
        didParseCell: (data) => {
          if (data?.section === 'body' && data?.column?.index === 2) {
            const val = data?.cell?.raw?.toLowerCase();
            data.cell.styles.textColor = val === 'pass' ? GREEN : val === 'fail' ? RED : TEXT_MID;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      }
    );

    // Overall result box
    h?.checkPageBreak(30, 'Audit Report', siteName);
    const isPass = audit?.overall_result === 'pass' || scoreRaw >= 60;
    doc?.setFillColor(...(isPass ? [220, 252, 231] : [254, 226, 226]));
    doc?.roundedRect(margin, doc?.lastAutoTable?.finalY + 6 || 200, contentW, 20, 2, 2, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...(isPass ? GREEN : RED));
    doc?.text(
      `Overall Result: ${scoreRaw}% — ${isPass ? 'PASS' : 'FAIL'}`,
      margin + 6,
      (doc?.lastAutoTable?.finalY || 200) + 20
    );

    // Signature block
    h?.checkPageBreak(60, 'Audit Report', siteName);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const filename = `siteproof-audit-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  /**
 * Generate a Near Miss Report PDF.
 * @param {Object} params
 * @param {Array}  params.nearMisses   - Array of near miss records
 * @param {string} params.siteName     - Site name (or 'All Sites')
 * @param {string} params.companyName  - Company name
 * @param {string} params.dateFrom     - Optional start date string
 * @param {string} params.dateTo       - Optional end date string
 */
  generateNearMissReport({ nearMisses = [], siteName = 'All Sites', companyName = 'SiteProof', dateFrom = null, dateTo = null } = {}) {
    const doc = createDoc();
    const margin = 14;
    const pageW = 210;
    const pageH = 297;
    const contentW = pageW - margin * 2;
    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    const fromLabel = dateFrom ? fmt(dateFrom) : 'All time';
    const toLabel = dateTo ? fmt(dateTo) : fmt(new Date()?.toISOString());
    const siteSlug = (siteName || 'all')?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];

    // ── Cover Page ────────────────────────────────────────────────────────────
    h?.addCoverPage('Near Miss Report', siteName, companyName, fromLabel, toLabel);

    // ── KPI Grid ──────────────────────────────────────────────────────────────
    const total = nearMisses?.length;
    const openCount = nearMisses?.filter(nm => nm?.status === 'Open')?.length;
    const underReviewCount = nearMisses?.filter(nm => nm?.status === 'Under review')?.length;
    const closedCount = nearMisses?.filter(nm => nm?.status === 'Closed')?.length;
    const highRiskCount = nearMisses?.filter(nm => nm?.risk_level === 'High')?.length;

    h?.addKPIGrid([
      { label: 'Total Near Misses', value: total, color: BRAND_ACCENT },
      { label: 'Open', value: openCount, color: RED },
      { label: 'Under Review', value: underReviewCount, color: AMBER },
      { label: 'Closed', value: closedCount, color: GREEN },
      { label: 'High Risk', value: highRiskCount, color: RED },
    ]);

    // ── Data Page ─────────────────────────────────────────────────────────────
    h?.startDataPage('Near Miss Report', siteName);
    h?.addSectionTitle('Near Miss Records');

    const tableRows = nearMisses?.map(nm => {
      const riskColor = nm?.risk_level === 'High' ? RED : nm?.risk_level === 'Medium' ? AMBER : GREEN;
      const statusColor = nm?.status === 'Open' ? RED : nm?.status === 'Under review' ? AMBER : GREEN;
      return [
        fmt(nm?.date),
        nm?.site?.site_name || '—',
        nm?.location || '—',
        nm?.description?.length > 80 ? nm?.description?.substring(0, 80) + '…' : (nm?.description || '—'),
        nm?.risk_level || '—',
        nm?.status || '—',
        nm?.reporter?.full_name || '—',
      ];
    });

    h?.addTable(
      ['Date', 'Site', 'Location', 'Description', 'Risk Level', 'Status', 'Reported By'],
      tableRows?.length > 0 ? tableRows : [['—', '—', '—', 'No records found', '—', '—', '—']],
      {
        headColor: BRAND_DARK,
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 28 },
          2: { cellWidth: 24 },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 18 },
          5: { cellWidth: 22 },
          6: { cellWidth: 26 },
        },
        didParseCell: (data) => {
          if (data?.section === 'body') {
            if (data?.column?.index === 4) {
              const val = data?.cell?.raw;
              data.cell.styles.textColor = val === 'High' ? RED : val === 'Medium' ? AMBER : GREEN;
              data.cell.styles.fontStyle = 'bold';
            }
            if (data?.column?.index === 5) {
              const val = data?.cell?.raw;
              data.cell.styles.textColor = val === 'Open' ? RED : val === 'Under review' ? AMBER : GREEN;
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
      }
    );

    // ── Signature Block ───────────────────────────────────────────────────────
    h?.checkPageBreak(60, 'Near Miss Report', siteName);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const filename = `siteproof-near-miss-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },

  // ── SITE AUDIT REPORT ─────────────────────────────────────────────────────
  async generateSiteAuditReport({ site, company, snags = [], documents = [], activities = [], tasks = [], photos = [] }) {
    const doc = createDoc();
    const pageW = doc?.internal?.pageSize?.getWidth();
    const pageH = doc?.internal?.pageSize?.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;

    const siteName = site?.site_name || 'Site Report';
    const companyName = company?.company_name || 'SiteProof';
    const today = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const siteSlug = siteName?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
    const dateSlug = new Date()?.toISOString()?.split('T')?.[0];

    const h = makeHelpers(doc, margin, contentW, pageW, pageH);

    // ── Cover Page ────────────────────────────────────────────────────────────
    h?.addCoverPage('Site Audit Report', siteName, companyName, 'All time', today);

    // Site details box
    doc?.setFillColor(...BG_LIGHT);
    doc?.roundedRect(margin, h?.getY(), contentW, 38, 2, 2, 'F');
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...TEXT_DARK);
    doc?.text('Site Details', margin + 4, h?.getY() + 8);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...TEXT_MID);
    const detailsY = h?.getY();
    const col1 = margin + 4;
    const col2 = margin + contentW / 2;
    doc?.text(`Site Name: ${siteName}`, col1, detailsY + 16);
    doc?.text(`Site Manager: ${site?.site_manager_name || '—'}`, col1, detailsY + 23);
    doc?.text(`Status: ${site?.status || '—'}`, col2, detailsY + 16);
    doc?.text(`Report Date: ${today}`, col2, detailsY + 23);
    doc?.text(`Company: ${companyName}`, col1, detailsY + 30);
    h?.setY(h?.getY() + 46);

    // ── KPI Summary ───────────────────────────────────────────────────────────
    const completedTasks = tasks?.filter(t => t?.status === 'completed')?.length;
    const openSnags = snags?.filter(s => s?.status === 'open' || s?.status === 'in_progress')?.length;
    const closedSnags = snags?.filter(s => s?.status === 'resolved' || s?.status === 'closed')?.length;
    const today8601 = new Date()?.toISOString()?.split('T')?.[0];
    const expiringDocs = documents?.filter(d => {
      if (!d?.expiry_date) return false;
      const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      return d?.expiry_date >= today8601 && d?.expiry_date <= thirtyDays;
    })?.length;

    h?.addKPIGrid([
      { label: 'Completed Tasks', value: completedTasks, color: GREEN },
      { label: 'Open Issues', value: openSnags, color: AMBER },
      { label: 'Closed Issues', value: closedSnags, color: BRAND_ACCENT },
      { label: 'Docs Expiring (30d)', value: expiringDocs, color: RED },
    ]);

    // ── Data Pages ────────────────────────────────────────────────────────────
    h?.startDataPage('Site Audit Report', siteName);
    h?.addPageHeader('Site Audit Report', siteName, 2);

    // Completed Tasks
    h?.addSectionTitle('Completed Tasks');
    const completedTaskRows = tasks
      ?.filter(t => t?.status === 'completed')
      ?.map(t => [
        t?.task_name || t?.title || '—',
        t?.category || '—',
        fmt(t?.task_date || t?.completed_at || t?.updated_at),
        t?.assigned_to_name || '—',
      ]);
    if (completedTaskRows?.length > 0) {
      h?.addTable(
        ['Task Name', 'Category', 'Completed Date', 'Assigned To'],
        completedTaskRows,
        { headColor: GREEN }
      );
    } else {
      doc?.setFontSize(8);
      doc?.setTextColor(...TEXT_MID);
      doc?.text('No completed tasks recorded for this site.', margin + 3, h?.getY());
      h?.setY(h?.getY() + 10);
    }

    // Open Issues / Snags
    h?.checkPageBreak(30, 'Site Audit Report', siteName);
    h?.addSectionTitle('Open Issues / Snags');
    const openSnagRows = snags
      ?.filter(s => s?.status === 'open' || s?.status === 'in_progress')
      ?.map(s => [
        s?.title || s?.snag_title || '—',
        s?.priority || '—',
        s?.status || '—',
        s?.assigned_to_name || '—',
        fmt(s?.created_at),
      ]);
    if (openSnagRows?.length > 0) {
      h?.addTable(
        ['Title', 'Priority', 'Status', 'Assigned To', 'Raised'],
        openSnagRows,
        { headColor: AMBER }
      );
    } else {
      doc?.setFontSize(8);
      doc?.setTextColor(...TEXT_MID);
      doc?.text('No open issues recorded.', margin + 3, h?.getY());
      h?.setY(h?.getY() + 10);
    }

    // Closed Issues / Snags
    h?.checkPageBreak(30, 'Site Audit Report', siteName);
    h?.addSectionTitle('Closed Issues / Snags');
    const closedSnagRows = snags
      ?.filter(s => s?.status === 'resolved' || s?.status === 'closed')
      ?.map(s => [
        s?.title || s?.snag_title || '—',
        s?.priority || '—',
        fmt(s?.resolved_at || s?.updated_at),
        s?.resolution_notes || '—',
      ]);
    if (closedSnagRows?.length > 0) {
      h?.addTable(
        ['Title', 'Priority', 'Resolved Date', 'Resolution Notes'],
        closedSnagRows,
        { headColor: GREEN }
      );
    } else {
      doc?.setFontSize(8);
      doc?.setTextColor(...TEXT_MID);
      doc?.text('No closed issues recorded.', margin + 3, h?.getY());
      h?.setY(h?.getY() + 10);
    }

    // Document Status
    h?.checkPageBreak(30, 'Site Audit Report', siteName);
    h?.addSectionTitle('Document Status');
    const docRows = documents?.map(d => {
      const expiry = d?.expiry_date;
      let status = 'Valid';
      if (expiry) {
        if (expiry < today8601) status = 'Expired';
        else {
          const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
          if (expiry <= thirtyDays) status = 'Expiring Soon';
        }
      }
      return [
        d?.title || '—',
        d?.category || '—',
        fmt(d?.expiry_date),
        status,
      ];
    });
    if (docRows?.length > 0) {
      h?.addTable(
        ['Document Name', 'Category', 'Expiry Date', 'Status'],
        docRows,
        {
          headColor: BRAND_DARK,
          didParseCell: (data) => {
            if (data?.section === 'body' && data?.column?.index === 3) {
              const val = data?.cell?.raw;
              if (val === 'Expired') data.cell.styles.textColor = RED;
              else if (val === 'Expiring Soon') data.cell.styles.textColor = AMBER;
              else data.cell.styles.textColor = GREEN;
            }
          }
        }
      );
    } else {
      doc?.setFontSize(8);
      doc?.setTextColor(...TEXT_MID);
      doc?.text('No documents uploaded for this site.', margin + 3, h?.getY());
      h?.setY(h?.getY() + 10);
    }

    // Uploaded Photos
    if (photos?.length > 0) {
      h?.checkPageBreak(30, 'Site Audit Report', siteName);
      h?.addSectionTitle('Uploaded Photos');
      const photoRows = photos?.map(p => [
        p?.caption || '—',
        fmt(p?.created_at),
        p?.task_id ? 'Task photo' : 'Site photo',
      ]);
      h?.addTable(
        ['Caption / Description', 'Uploaded Date', 'Type'],
        photoRows,
        { headColor: BRAND_ACCENT }
      );
    }

    // Activity Timeline
    h?.checkPageBreak(30, 'Site Audit Report', siteName);
    h?.addSectionTitle('Activity Timeline');
    const activityRows = activities
      ?.slice(0, 30)
      ?.map(a => [
        fmt(a?.created_at),
        a?.activity_type?.replace(/_/g, ' ') || '—',
        a?.title || '—',
        a?.user?.full_name || '—',
      ]);
    if (activityRows?.length > 0) {
      h?.addTable(
        ['Date', 'Type', 'Description', 'User'],
        activityRows,
        { headColor: BRAND_DARK }
      );
    } else {
      doc?.setFontSize(8);
      doc?.setTextColor(...TEXT_MID);
      doc?.text('No activity recorded for this site.', margin + 3, h?.getY());
      h?.setY(h?.getY() + 10);
    }

    // Signature block
    h?.checkPageBreak(60, 'Site Audit Report', siteName);
    h?.addSignatureBlock(companyName);
    h?.addPageFooter();

    const filename = `siteproof-audit-${siteSlug}-${dateSlug}.pdf`;
    doc?.save(filename);
    return { filename, error: null };
  },
};
